import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Cpu,
  Bot,
  BarChart3,
  Workflow,
  Database,
  Cloud,
  Target,
  Megaphone,
  Share2,
  Code2,
  MousePointerClick,
  FlaskConical,
  Layers,
  ArrowRight
} from 'lucide-react';
import '../styles/Services.css';

const Services = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location]);

  const aiOfferings = [
    {
      icon: <Sparkles />,
      title: 'AI Roadmapping',
      description: 'Cut through the hype and get a clear, actionable AI plan.'
    },
    {
      icon: <Bot />,
      title: 'Agentic AI',
      description: 'Autonomous agents that plan, act, and execute across real workflows.'
    },
    {
      icon: <Workflow />,
      title: 'Automation & Efficiency Design',
      description: 'Build workflows that save hours (and sanity).'
    },
    {
      icon: <Cpu />,
      title: 'AI Product Development',
      description: 'Blueprints → features → working AI-powered products.'
    },
    {
      icon: <BarChart3 />,
      title: 'Data Science',
      description: 'Modelling, BI, analytics — turn data into decisions (not dashboards you never open).'
    },
    {
      icon: <Database />,
      title: 'Data Engineering',
      description: 'Pipelines, warehouses, and foundations that make AI and analytics reliable.'
    },

  ];

  const gtmOfferings = [
    {
      icon: <Target />,
      title: 'GTM Strategy & Planning',
      description: 'Clear positioning and a launch & growth roadmap that’s actually usable.',
      bullets: [
        'Clear positioning',
        'Audience segmentation',
        'Value proposition design',
        'Launch & growth roadmap',
        'Pricing frameworks'
      ],
      note: 'Smart moves, backed by data and clarity.'
    },
    {
      icon: <Megaphone />,
      title: 'Marketing Strategy',
      description: 'Brand voice, narrative, and messaging — delivered simply and instantly usable.',
      bullets: ['Brand storytelling', 'Campaign planning', 'Creative direction', 'Channel strategy']
    },
    {
      icon: <Share2 />,
      title: 'Social Media Growth & Content',
      description: 'Stop posting randomly. Start posting with purpose.',
      bullets: [
        'Social media strategy',
        'Content calendars',
        'Creatives + copy',
        'Engagement systems',
        'Thought leadership positioning'
      ]
    },
    {
      icon: <Code2 />,
      title: 'Website Development',
      description: 'Modern, clean, conversion-first websites people actually want to scroll.',
      bullets: ['UX + UI', 'Copywriting', 'Webflow/WordPress build', 'SEO basics', 'Landing page optimisation']
    },
    {
      icon: <BarChart3 />,
      title: 'Performance Marketing',
      description: 'Measurable, trackable results — no guesswork.',
      bullets: ['Meta ads', 'Google ads', 'Retargeting', 'Campaign optimisation', 'Funnel tracking + analytics']
    },
    {
      icon: <MousePointerClick />,
      title: 'Funnel & Conversion Systems',
      description: 'Turn strangers → followers → users → loyalists.',
      bullets: ['Email nurturing', 'Lead magnets', 'Landing pages', 'Sales funnels', 'Content engines', 'Retention systems']
    },
    {
      icon: <FlaskConical />,
      title: 'PMF Experiments',
      description: 'Quick, low-cost tests to see if your product, audience, and value truly fit together.',
      pmf: {
        why: [
          'Are we solving a real problem?',
          'Do people understand the value?',
          'Who wants this the most?',
          'What will they pay for it?',
          'Which features matter first?'
        ],
      },
      note: 'PMF = the sweet spot where your product and the market finally vibe.'
    },
    {
      icon: <Layers />,
      title: 'Content Engines',
      description: 'Your 24/7 storytelling machine.',
      bullets: ['Blogs', 'Playbooks', 'Videos', 'Carousels', 'Short-form content', 'Repurposing systems']
    }
  ];

  const ServiceCard = ({ item, variant, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ duration: 0.45, delay: index * 0.04 }}
      className={`service-detail-card ${variant === 'gtm' ? 'is-gtm' : 'is-ai'}`}
    >
      <div className="service-detail-header">
        <div className={`service-detail-icon ${variant === 'gtm' ? 'gtm-icon' : 'ai-icon'}`}>{item.icon}</div>
        <div className="service-detail-heading">
          <h3 className="service-detail-title">{item.title}</h3>
        </div>
      </div>

      <p className="service-detail-description">{item.description}</p>

      {Array.isArray(item.bullets) && item.bullets.length > 0 && (
        <ul className="service-detail-features">
          {item.bullets.map((b) => (
            <li key={b}>
              <ArrowRight size={18} />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      {item.pmf && (
        <div className="pmf-wrap">
          <div className="pmf-section">
            <h4>Why we run PMF experiments</h4>
            <ul className="service-detail-features">
              {item.pmf.why.map((b) => (
                <li key={b}>
                  <ArrowRight size={18} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>


        </div>
      )}

      {item.note && <div className="service-detail-note">{item.note}</div>}
    </motion.div>
  );

  const MainSection = ({ id, variant, badgeIcon, badgeText, title, description, items, ctaText, ctaHref }) => (
    <section id={id} className={`services-main-section ${variant === 'gtm' ? 'gtm-services-section' : 'ai-services-section'}`}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.55 }}
          className="services-header"
        >
          <div className={`services-badge ${variant === 'gtm' ? 'gtm' : ''}`}>
            {badgeIcon}
            {badgeText}
          </div>
          <h2 className="services-title">{title}</h2>
          <p className="services-description">{description}</p>
        </motion.div>

        <div className={`services-grid ${variant === 'gtm' ? 'services-grid--gtm' : 'services-grid--ai'}`}>
          {items.map((item, index) => (
            <ServiceCard item={item} variant={variant} index={index} key={item.title} />
          ))}
        </div>

        {ctaText && (
          <div className={`services-cta ${variant === 'gtm' ? 'services-cta--gtm' : 'services-cta--ai'}`}>
            <a className="services-cta-btn" href={ctaHref || '/contact'}>
              {ctaText} <ArrowRight size={18} />
            </a>
          </div>
        )}
      </div>
    </section>
  );

  return (
    <div className="services-page">
      {/* HERO */}
      <section className="services-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="services-hero-content"
          >
            <h1 className="services-hero-title">Clean, simple offerings. Real results.</h1>
            <p className="services-hero-subtitle">Choose your galaxy ↓</p>

            <div className="services-galaxy-nav" role="navigation" aria-label="Services sections">
              <a className="galaxy-pill" href="#ai">
                AI Advisory & Consulting
              </a>
              <a className="galaxy-pill" href="#gtm">
                GTM Consulting & Marketing
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* A) AI Advisory & Consulting */}
      <MainSection
        id="ai"
        variant="ai"
        title="AI Advisory & Consulting"
        description="Practical AI that ships — from roadmaps and agentic workflows to integrated AI products."
        items={aiOfferings}
      />

      {/* B) GTM Consulting & Marketing */}
      <MainSection
        id="gtm"
        variant="gtm"
        title="GTM Consulting & Marketing"
        description="A sharp, modern GTM engine built for fast-moving teams — launch, grow, and dominate your space."
        items={gtmOfferings}
        ctaText="Not sure what you need? We’ll help you figure it out"
        ctaHref="/contact"
      />
    </div>
  );
};

export default Services;